import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from '../../stores/authStore'
import MobileErrorBoundary from './MobileErrorBoundary'
import SwUpdateBanner from '../SwUpdateBanner'
// Phase R UX H-3: 오프라인 인지 — 전체 모바일 인증 라우트에 글로벌 노출
import useOfflineSync, { SyncStatus } from '../../hooks/useOfflineSync'
// PWA 앱 아이콘 배지 — 홈 화면 PWA 는 Layout 을 렌더하지 않으므로 여기서 붙인다
import useAppBadge from '../../hooks/useAppBadge'
// Phase BA: QR-only 접근 가드 — toast 알림

/**
 * MobileAuthGuard - 모바일 라우트 인증 보호 레이아웃
 *
 * 인증되지 않은 사용자가 /mobile/* 경로 접근 시 /login으로 리다이렉트
 * 협력사 active_role 과 모바일 대시보드 URL 불일치 시 자동 리다이렉트
 */
export default function MobileAuthGuard({ allowedRoles = [] }) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  // Phase R UX H-3: 오프라인 상태 + 대기 큐 추적 (모든 모바일 페이지 자동)
  const { online, syncing, pendingCount, processQueue } = useOfflineSync()

  // 홈 화면 아이콘 미읽음 배지 (PC Layout 과 동일 훅)
  useAppBadge()

  // 양쪽 capability 협력사: active_role 과 모바일 대시보드 경로 불일치 시 자동 이동
  useEffect(() => {
    const ADMIN = ['system_admin', 'mold_developer', 'staff']
    if (!user || ADMIN.includes(user.user_type)) return
    const role = user.active_role
    if (!role) return
    const path = location.pathname
    if (path.startsWith('/mobile/dashboard/')) {
      const expected = role === 'plant' ? '/mobile/dashboard/plant'
                     : role === 'maker' ? '/mobile/dashboard/maker'
                     : null
      if (expected && path !== expected) {
        navigate(expected, { replace: true })
      }
    }
  }, [user?.active_role, location.pathname, navigate, user])

  // 미인증 → 로그인
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // 2026-10-13 사용자 명시: "할당된 금형에 대해 PC 버전과 동일한 권한" —
  //   협력사 금형 목록 차단을 푼다.
  //
  //   종전(Phase BA/BH)에는 협력사가 /mobile/molds 로 들어오면 "QR 스캔으로 접근해 주세요"
  //   안내 후 qr-login 으로 돌려보냈다. 그래서 홈 빠른작업의 '금형목록' 버튼도 늘 튕겼고,
  //   사용자가 보고한 "모바일에서 금형목록 조회가 안 된다" 가 이것이었다.
  //   PC 는 PLANT_MENU/MAKER_MENU 에 '금형 목록' 이 정식으로 있다.
  //
  //   현장 실물 확인(QR 재스캔)이라는 원래 목적은 **점검 진입 게이트**가 대신 맡는다
  //   (lib/inspectionQrGate — 일상·정기·습합·세척은 QR 을 찍어야 들어간다).
  //   목록 조회 자체는 막지 않는다. 회사 격리는 서버가 강제하므로(Phase BC/L1)
  //   협력사는 목록에서도 자사 금형만 본다.
  //
  //   금형 상세 직접 접근은 종전대로 막지 않는다 — 백엔드 회사 필터가 권한을 강제하고,
  //   과거 클라이언트 sessionStorage 검증은 race condition 으로 정상 흐름까지 차단해 제거됐다.

  // 역할 제한 (allowedRoles): user_type 과 active_role 모두 허용
  if (allowedRoles.length > 0 && user) {
    const effectiveRole = user.active_role || user.user_type
    if (!allowedRoles.includes(user.user_type) && !allowedRoles.includes(effectiveRole)) {
      return <Navigate to="/mobile/home" replace />
    }
  }

  return (
    <MobileErrorBoundary>
      {/* Phase CL-B M-2: SW 업데이트 안내 배너 */}
      <SwUpdateBanner />
      {/* Phase R UX H-3: 오프라인/큐 상태 글로벌 배너 (online && pendingCount === 0 시 자동 숨김) */}
      <SyncStatus online={online} syncing={syncing} pendingCount={pendingCount} onSync={processQueue} />
      <Outlet />
    </MobileErrorBoundary>
  )
}
